import React from "react";
import cloneDeep from "lodash/cloneDeep";
import i18n from "i18n";
import { ClearFloat, Icon } from "components/common";
import { Button, ButtonToolbar, Col, FormGroup } from "components/bootstrap";
import {
  emptyBooleanExpressionConfig,
  emptyGroupExpressionConfig,
  replaceBooleanExpressionOperatorInGroup
} from "logic/alerts/AggregationExpressionConfig";
import NumberExpression from "./AggregationConditionExpressions/NumberExpression";
import NumberRefExpression from "./AggregationConditionExpressions/NumberRefExpression";
import BooleanOperatorSelector from "./AggregationConditionExpressions/BooleanOperatorSelector";
import BooleanExpression from "./AggregationConditionExpressions/BooleanExpression";
import ComparisonExpression from "./AggregationConditionExpressions/ComparisonExpression";
import GroupExpression from "./AggregationConditionExpressions/GroupExpression";
import styles from "./AggregationConditionExpression.css";
class AggregationConditionExpression extends React.Component {
  constructor(props) {
    super(props);
    this.getEffectiveGlobalGroupOperator = () => {
      const { globalGroupOperator } = this.state;
      if (globalGroupOperator) {
        return globalGroupOperator;
      }
      const { expression } = this.props;
      return expression.expr === "&&" || expression.expr === "||" ? expression.expr : "&&";
    };
    // eslint-disable-next-line class-methods-use-this
    this.getBooleanOperator = (expression, defaultOperator) => {
      var _a;
      if (!expression) {
        return defaultOperator;
      }
      const key = expression.expr === "group" ? "operator" : "expr";
      return ((_a = expression == null ? void 0 : expression[key]) != null ? _a : defaultOperator) === "&&" ? "&&" : "||";
    };
    this.handleAddExpression = () => {
      const { expression, onChange, parent } = this.props;
      const defaultOperator = this.getEffectiveGlobalGroupOperator();
      const prevOperator = this.getBooleanOperator(parent, defaultOperator);
      const nextExpression = emptyBooleanExpressionConfig({ operator: prevOperator, left: expression });
      onChange({ conditions: nextExpression });
    };
    this.handleAddGroup = () => {
      const { expression, onChange, parent } = this.props;
      const defaultOperator = this.getEffectiveGlobalGroupOperator();
      const prevOperator = this.getBooleanOperator(parent, defaultOperator);
      const groupOperator = prevOperator === "&&" ? "||" : "&&";
      const groupExpression = emptyGroupExpressionConfig({ operator: groupOperator });
      const nextExpression = emptyBooleanExpressionConfig({
        operator: prevOperator,
        left: expression,
        right: groupExpression
      });
      onChange({ conditions: nextExpression });
    };
    this.handleDeleteExpression = () => {
      const { onChange } = this.props;
      onChange({ conditions: null });
    };
    this.handleChildChange = (branch) => (changes) => {
      const { expression, onChange } = this.props;
      if (!Object.keys(changes).includes("conditions")) {
        onChange(changes);
        return;
      }
      const update = changes.conditions;
      let nextUpdate;
      if (update === null) {
        if (branch === "child") {
          nextUpdate = null;
        } else {
          nextUpdate = expression[branch === "left" ? "right" : "left"];
        }
      } else if (branch === "child" && update.expr === "group") {
        nextUpdate = update;
      } else {
        const nextExpression = cloneDeep(expression);
        nextExpression[branch] = update;
        nextUpdate = nextExpression;
      }
      onChange({ ...changes, conditions: nextUpdate });
    };
    this.handleOperatorChange = (nextOperator) => {
      const { expression, onChange } = this.props;
      this.setState({ globalGroupOperator: nextOperator });
      const nextExpression = replaceBooleanExpressionOperatorInGroup(nextOperator, expression);
      onChange({ conditions: nextExpression });
    };
    this.state = {
      globalGroupOperator: void 0
    };
  }
  render() {
    const { expression, parent, renderLabel, level } = this.props;
    let expressionComponent;
    switch (expression.expr) {
      case "number-ref":
        expressionComponent = /* @__PURE__ */ React.createElement(NumberRefExpression, { ...this.props, renderLabel: renderLabel != null ? renderLabel : true });
        break;
      case "number":
        expressionComponent = /* @__PURE__ */ React.createElement(NumberExpression, { ...this.props, renderLabel: renderLabel != null ? renderLabel : true });
        break;
      case "group":
        expressionComponent = /* @__PURE__ */ React.createElement(GroupExpression, { ...this.props, level: level != null ? level : 0, onChildChange: this.handleChildChange, parent });
        break;
      case "&&":
      case "||":
        expressionComponent = /* @__PURE__ */ React.createElement(
          BooleanExpression,
          {
            ...this.props,
            level: level != null ? level : 0,
            onChildChange: this.handleChildChange,
            parent
          }
        );
        break;
      case "<":
      case "<=":
      case ">":
      case ">=":
      case "==":
      default:
        expressionComponent = /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
          ComparisonExpression,
          {
            ...this.props,
            level: level != null ? level : 0,
            renderLabel: renderLabel != null ? renderLabel : true,
            onChildChange: this.handleChildChange,
            parent
          }
        ), /* @__PURE__ */ React.createElement(Col, { md: 2 }, /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement("div", { className: renderLabel ? styles.formControlNoLabel : void 0 }, /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(
          Button,
          {
            bsSize: "sm",
            onClick: this.handleDeleteExpression,
            title: i18n.t("conditions.delete_expression", { ns: "event_definitions" })
          },
          /* @__PURE__ */ React.createElement(Icon, { name: "remove" })
        ), /* @__PURE__ */ React.createElement(
          Button,
          {
            bsSize: "sm",
            onClick: this.handleAddExpression,
            title: i18n.t("conditions.add_expression", { ns: "event_definitions" })
          },
          /* @__PURE__ */ React.createElement(Icon, { name: "add" })
        ), /* @__PURE__ */ React.createElement(Button, { bsSize: "sm", onClick: this.handleAddGroup }, i18n.t("conditions.add_group", { ns: "event_definitions" })))))));
    }
    if (!parent && expression.expr !== "group") {
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        BooleanOperatorSelector,
        {
          initialText: i18n.t("conditions.messages_must_meet", { ns: "event_definitions" }),
          placeholder: i18n.t("conditions.global_boolean_operator", { ns: "event_definitions" }),
          operator: this.getEffectiveGlobalGroupOperator(),
          onOperatorChange: this.handleOperatorChange
        }
      ), /* @__PURE__ */ React.createElement(ClearFloat, null), expressionComponent);
    }
    return expressionComponent;
  }
}
AggregationConditionExpression.defaultProps = {
  level: 0,
  parent: void 0,
  renderLabel: true,
  validation: {}
};
export default AggregationConditionExpression;
